import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Avatar, Btn, FilterSelect, Pagination, SkeletonText, useToast } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useAdminUsers, useFetch } from '@/hooks';
import { fetchAuditEvents, fetchRoles, updateAdminUser } from '@/api/admin';
import type { AuditEvent } from '@/api/admin';
import type { AdminUser, RoleListResponse } from '@/api/types';
import { getErrorMessage } from '@/api';
import { emailLocal, formatTimestamp } from '@/lib/format';
import { useMetaContext } from '@/components/Layout';

import { RoleSelect } from './admin/RoleSelect';
import { RolesTab } from './admin/RolesTab';
import { buildRoleOptions, roleTone } from './admin/roleOptions';
import {
  ALL,
  STATUS_OPTIONS,
  URL_KEYS,
  displayName,
  filterByRole,
  filterExceptRole,
  groupOptions,
  readFilterState,
  teamOptions,
} from './admin/userFilters';
import type { UserFilterState } from './admin/userFilters';
import roleStyles from './admin/RoleSelect.module.css';
import styles from './Admin.module.css';

// plan §5.20 / §5.21: PageAdmin — 탭 구성.
// [사용자] 역할 다중 선택(Admin + Leader/Member/커스텀) + department 편집.
// [역할별 권한] 커스텀 역할 생성·수정·삭제 + 역할마다 '접근 메뉴 설정' (admin/pages/admin/RolesTab).
// 역할을 모두 해제하면 guest — access_grants 전부 삭제되므로 confirm 다이얼로그 필요.

// 사용자 목록 페이지 크기 — Peoply 동기화로 280+명이 들어와 페이지네이션 필수.
const USERS_PAGE_SIZE = 20;

type TabKey = 'users' | 'roles' | 'audit';

interface ConfirmState {
  user: AdminUser;
  nextRoles: string[];
}

export default function AdminPage() {
  const meta = useMetaContext();
  const toast = useToast();

  const [tab, setTab] = useState<TabKey>('users');

  // 검색·필터는 URL 쿼리가 단일 소스 — 필터 걸린 화면을 링크로 공유할 수 있다.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilterState(searchParams);
  const [page, setPage] = useState(1);
  // 전체 목록을 한 번 받아 검색·필터는 클라이언트에서 건다(300명 안팎이라 즉시 반영).
  const adminUsers = useAdminUsers();
  const rolesState = useFetch<RoleListResponse>((signal) => fetchRoles(signal), []);
  const roleOptions = useMemo(() => buildRoleOptions(rolesState.data?.roles ?? []), [rolesState.data]);

  const updateFilters = (patch: Partial<UserFilterState>) => {
    const next = { ...filters, ...patch };
    const params = new URLSearchParams(searchParams);
    for (const key of URL_KEYS) {
      const value = next[key];
      if (value && value !== ALL) params.set(key, value);
      else params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };
  const hasFilters = URL_KEYS.some((key) => filters[key] && filters[key] !== ALL);

  useEffect(() => {
    if (rolesState.error) toast.error(getErrorMessage(rolesState.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesState.error]);

  // 로컬 편집 상태 (department editor)
  const [draftDept, setDraftDept] = useState<Record<string, string>>({});
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // 응답이 바뀌면 draftDept를 reset (서버 값 우선)
  useEffect(() => {
    if (!adminUsers.data) return;
    const next: Record<string, string> = {};
    for (const u of adminUsers.data.users) {
      next[u.email] = u.department ?? '';
    }
    setDraftDept(next);
  }, [adminUsers.data]);

  const allUsers = adminUsers.data?.users ?? [];
  // 역할 외 조건(검색·그룹·팀·상태)을 먼저 건 목록 — 역할별 인원 카드는 이 안에서 센다.
  // 300명 안팎이라 렌더마다 걸러도 충분히 가볍다.
  const scopedUsers = filterExceptRole(allUsers, filters);
  const users = filterByRole(scopedUsers, filters.role);
  const groupOpts = useMemo(() => groupOptions(allUsers), [allUsers]);
  const teamOpts = useMemo(() => teamOptions(allUsers, filters.group), [allUsers, filters.group]);

  // 검색어/필터가 바뀌면 1페이지로 리셋.
  const filterKey = searchParams.toString();
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  // 클라이언트 페이지네이션 — 목록은 서버에서 받은 전체를 여기서 자른다.
  const pagedUsers = users.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);

  const countOf = (key: string) =>
    key === 'none'
      ? scopedUsers.filter((u) => u.roles.length === 0).length
      : scopedUsers.filter((u) => u.roles.includes(key)).length;

  const labelOf = (key: string) => roleOptions.find((o) => o.key === key)?.label ?? key;

  const onChangeRoles = async (user: AdminUser, nextRoles: string[]) => {
    if (user.email === meta.current_user.email && user.roles.includes('admin') && !nextRoles.includes('admin')) {
      toast.error('본인의 Admin 권한은 해제할 수 없어요');
      return;
    }
    if (nextRoles.length === 0 && user.grants_count > 0) {
      setConfirm({ user, nextRoles });
      return;
    }
    await applyRoles(user, nextRoles);
  };

  // 서버 재조회 없이 목록에서 한 사용자만 교체 — 낙관적 업데이트/확정 반영 공용.
  const patchUserLocal = (email: string, patch: Partial<AdminUser>) =>
    adminUsers.mutate((prev) =>
      prev
        ? { ...prev, users: prev.users.map((u) => (u.email === email ? { ...u, ...patch } : u)) }
        : prev,
    );

  const applyRoles = async (user: AdminUser, nextRoles: string[]) => {
    setBusyEmail(user.email);
    setConfirm(null);
    // 낙관적 업데이트 — 적용 즉시 칩 반영(전체 reload 없이 반응성 확보).
    patchUserLocal(user.email, { roles: nextRoles });
    try {
      const updated = await updateAdminUser(user.email, { roles: nextRoles });
      patchUserLocal(user.email, updated); // 서버 확정값으로 그 사용자만 교체.
      const summary = nextRoles.length ? nextRoles.map(labelOf).join(', ') : 'Guest';
      toast.success(`${emailLocal(user.email)}님의 역할을 ${summary}(으)로 변경했어요`);
      rolesState.reload(); // 역할별 인원 수 갱신
    } catch (err) {
      toast.error(getErrorMessage(err));
      adminUsers.reload(); // 실패 시 서버 상태로 롤백.
    } finally {
      setBusyEmail(null);
    }
  };

  const onCommitDept = async (user: AdminUser) => {
    const next = (draftDept[user.email] ?? '').trim();
    const current = user.department ?? '';
    if (next === current) return;
    setBusyEmail(user.email);
    // 낙관적 업데이트 — 입력 확정 즉시 반영.
    patchUserLocal(user.email, { department: next === '' ? null : next });
    try {
      const updated = await updateAdminUser(user.email, { department: next === '' ? null : next });
      patchUserLocal(user.email, updated);
      toast.success(`${emailLocal(user.email)}님 부서를 갱신했어요`);
    } catch (err) {
      toast.error(getErrorMessage(err));
      adminUsers.reload(); // 실패 시 롤백.
    } finally {
      setBusyEmail(null);
    }
  };

  const filterKeys = [...roleOptions.map((o) => o.key), 'none'];

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>사용자 관리</h1>
      <div className={styles.subtitle}>
        Google로 로그인한 사용자 목록입니다. 신규 사용자는 역할이 없는 <b style={{ color: 'var(--sc-text)' }}>Guest</b>이며,
        역할을 하나 이상 부여해야 화면 접근이 가능합니다.
      </div>

      {/* 탭 */}
      <div className={styles.tabRow}>
        <button
          type="button"
          className={`${styles.tabBtn} ${tab === 'users' ? styles.tabOn : ''}`}
          onClick={() => setTab('users')}
        >
          사용자
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${tab === 'roles' ? styles.tabOn : ''}`}
          onClick={() => setTab('roles')}
        >
          역할별 권한
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${tab === 'audit' ? styles.tabOn : ''}`}
          onClick={() => setTab('audit')}
        >
          감사 로그
        </button>
      </div>

      {tab === 'users' && (
        <>
          {/* 역할별 인원 — 누르면 그 역할로 필터 */}
          <div className={styles.statsRow}>
            {filterKeys.map((key) => {
              const option = roleOptions.find((o) => o.key === key);
              const tone = option ? roleTone(option) : 'toneGuest';
              return (
                <Stat
                  key={key}
                  label={option?.label ?? 'Guest'}
                  count={countOf(key)}
                  toneClass={roleStyles[tone]}
                  active={filters.role === key}
                  onClick={() => updateFilters({ role: filters.role === key ? ALL : key })}
                />
              );
            })}
          </div>

          {/* 툴바 */}
          <div className={styles.toolbar}>
            <div className={styles.filterRow}>
              <button
                type="button"
                className={`${styles.filter} ${filters.role === ALL ? styles.active : ''}`}
                onClick={() => updateFilters({ role: ALL })}
              >
                전체
              </button>
              {filterKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.filter} ${filters.role === key ? styles.active : ''}`}
                  onClick={() => updateFilters({ role: key })}
                >
                  {key === 'none' ? 'Guest' : labelOf(key)}
                </button>
              ))}
            </div>
            <div className={styles.searchWrap}>
              <NavIcon name="search" size={14} color="#9B9B9B" />
              <input
                className={styles.searchInput}
                type="search"
                value={filters.q}
                onChange={(e) => updateFilters({ q: e.target.value })}
                placeholder="이름·이메일·팀 검색"
                aria-label="사용자 검색"
              />
            </div>
          </div>

          {/* Peoply 소속·상태 필터 */}
          <div className={styles.orgFilterRow}>
            <FilterSelect
              label="그룹"
              value={filters.group}
              options={groupOpts}
              onChange={(group) => updateFilters({ group, team: ALL })}
            />
            <FilterSelect
              label="팀"
              value={filters.team}
              options={teamOpts}
              onChange={(team) => updateFilters({ team })}
            />
            <FilterSelect
              label="상태"
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(status) => updateFilters({ status: status as UserFilterState['status'] })}
            />
            {/* 첫 로드 전엔 '0 / 0명' 이 깜빡이지 않게 숨긴다. */}
            {adminUsers.data && (
              <span className={styles.resultCount}>
                <b>{users.length}</b> / {allUsers.length}명
              </span>
            )}
            {hasFilters && (
              <button
                type="button"
                className={styles.resetBtn}
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
              >
                필터 초기화
              </button>
            )}
          </div>

          {/* 테이블 */}
          <div className={styles.table}>
            <div className={styles.tableHead}>
              <span>사용자</span>
              <span>소속 (Peoply)</span>
              <span>부서 (수동)</span>
              <span>최근 로그인</span>
              <span style={{ textAlign: 'right' }}>역할</span>
            </div>
            {/* 첫 로드에만 스켈레톤 — 검색/필터 재조회 중엔 기존 목록을 유지해 깜빡임 방지. */}
            {adminUsers.loading && allUsers.length === 0 && (
              <SkeletonText lines={5} lastWidth="40%" gap={12} lineHeight={14} />
            )}
            {!adminUsers.loading && users.length === 0 && (
              <div className={styles.empty}>일치하는 사용자가 없어요</div>
            )}
            <div
              className={styles.rowsFade}
              data-loading={adminUsers.loading && allUsers.length > 0 ? 'true' : 'false'}
            >
            {pagedUsers.map((u) => {
              const isNew = u.roles.length === 0;
              const isSelf = u.email === meta.current_user.email;
              const busy = busyEmail === u.email;
              const name = displayName(u, emailLocal(u.email));
              return (
                <div key={u.email} className={`${styles.tableRow} ${isNew ? styles.isNew : ''}`}>
                  <div className={styles.userCell}>
                    <Avatar email={u.email} name={name.primary} size={36} />
                    <div style={{ minWidth: 0 }}>
                      <div className={styles.userName}>
                        {name.primary}
                        {name.secondary && <span className={styles.realName}>{name.secondary}</span>}
                        {isSelf && <span className={styles.tag}>나</span>}
                        {isNew && <span className={styles.tag} style={{ background: '#FFF1D6', color: '#8A6516' }}>신규</span>}
                        {u.has_all_scope && <span className={styles.tag} style={{ background: '#EDE6FF', color: '#4A1FB0' }}>all</span>}
                      </div>
                      <div className={styles.userEmail}>{u.email}</div>
                    </div>
                  </div>
                  {/* Peoply 소속 — 동기화된 읽기 전용 값 (부서 + 리더 뱃지) */}
                  <div className={styles.peoplyCell}>
                    {u.peoply_department ? (
                      <>
                        <div className={styles.peoplyOrg}>
                          <span className={styles.peoplyDept}>{u.peoply_team ?? u.peoply_group ?? u.peoply_department}</span>
                          {/* 팀 위 그룹·직무를 작게 — 팀 이름만으론 어느 그룹인지 모를 때가 많다. */}
                          {(u.peoply_team || u.peoply_job) && (
                            <span className={styles.peoplySub}>
                              {[u.peoply_team ? u.peoply_group : null, u.peoply_job].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </div>
                        {u.peoply_is_leader && <span className={styles.leaderBadge}>리더</span>}
                      </>
                    ) : (
                      <span className={styles.peoplyNone}>—</span>
                    )}
                  </div>
                  {/* 수동 부서 — redash sync 등에 쓰이는 편집 가능한 값 */}
                  <div className={styles.deptCell}>
                    <input
                      className={styles.deptInput}
                      value={draftDept[u.email] ?? ''}
                      onChange={(e) =>
                        setDraftDept((prev) => ({ ...prev, [u.email]: e.target.value }))
                      }
                      onBlur={() => onCommitDept(u)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="부서 (예: pm)"
                      disabled={busy}
                    />
                  </div>
                  {/* 최근 로그인 */}
                  <span className={styles.loginCell}>{formatTimestamp(u.last_login_at)}</span>
                  <RoleSelect
                    value={u.roles}
                    options={roleOptions}
                    onApply={(next) => void onChangeRoles(u, next)}
                    disabled={busy || !rolesState.data}
                    lockAdmin={isSelf}
                  />
                </div>
              );
            })}
            </div>
          </div>

          {/* 페이지네이션 */}
          {users.length > USERS_PAGE_SIZE && (
            <Pagination
              page={page}
              total={users.length}
              pageSize={USERS_PAGE_SIZE}
              onChange={setPage}
            />
          )}
        </>
      )}

      {tab === 'roles' && (
        <RolesTab
          roles={rolesState.data?.roles ?? null}
          onRolesChanged={() => {
            rolesState.reload();
            adminUsers.reload(); // 역할 삭제·이름 변경이 사용자 칩에도 반영되도록
          }}
        />
      )}

      {tab === 'audit' && <AuditSection />}

      {/* guest 확인 모달 */}
      {confirm && (
        <div className={styles.modalBackdrop} onClick={() => setConfirm(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>권한 회수 확인</div>
            <div className={styles.modalBody}>
              {emailLocal(confirm.user.email)}님은 현재 {confirm.user.grants_count}개의 권한을 보유 중입니다.
              <br />
              <b>역할을 모두 해제하면(Guest) 모든 권한이 즉시 회수</b>되며, BigQuery 접근도 제거됩니다.
              <br />
              계속 진행할까요?
            </div>
            <div className={styles.modalActions}>
              <Btn variant="default" onClick={() => setConfirm(null)}>
                취소
              </Btn>
              <Btn variant="danger" onClick={() => applyRoles(confirm.user, confirm.nextRoles)}>
                권한 회수
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface StatProps {
  label: string;
  count: number;
  toneClass: string;
  active: boolean;
  onClick: () => void;
}

function Stat({ label, count, toneClass, active, onClick }: StatProps) {
  return (
    <button
      type="button"
      className={`${styles.stat} ${toneClass} ${active ? styles.statActive : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span className={styles.statDot} />
      <span style={{ minWidth: 0, textAlign: 'left' }}>
        <span className={styles.statCount}>{count}</span>
        <span className={styles.statLabel}>{label}</span>
      </span>
    </button>
  );
}

// 감사 로그 이벤트 종류 한글 라벨.
const AUDIT_EVENT_LABELS: Record<string, string> = {
  role_change: '사용자 역할 변경',
  role_menu_map_change: '역할별 메뉴 권한 변경',
  platform_role_change: '사용자 역할 변경',
  user_roles_change: '사용자 역할 변경',
  role_definition_change: '역할 정의 변경',
  api_token_request: 'API 토큰 신청',
  api_token_reveal: 'API 토큰 확인',
  api_token_revoke: 'API 토큰 폐기',
};

// 감사 로그 탭 — 권한·토큰 변경의 DB 영구 기록 (P1-LOG-004).
function AuditSection() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      fetchAuditEvents(q.trim(), ctrl.signal)
        .then((res) => setEvents(res.events))
        .catch((err) => {
          if (!(err instanceof DOMException && err.name === 'AbortError')) {
            toast.error(getErrorMessage(err));
          }
        })
        .finally(() => setLoading(false));
    }, q ? 250 : 0); // 검색어 입력 시 디바운스
    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div>
      <div className={styles.toolbar} style={{ marginTop: 16 }}>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="이벤트·수행자·대상 검색"
          aria-label="감사 로그 검색"
          style={{
            width: 260, height: 34, padding: '0 10px',
            border: '1px solid var(--ui-input-border)', borderRadius: 8,
            background: 'var(--sc-white)', font: '500 12px/1 var(--sc-font-body)',
          }}
        />
        <span style={{ color: 'var(--sc-text-muted)', font: '500 11.5px/1.3 var(--sc-font-body)' }}>
          최근 200건 · DB 영구 보관
        </span>
      </div>

      {loading && !events ? (
        <div style={{ padding: 20 }}><SkeletonText lines={6} gap={14} /></div>
      ) : (
        <div className={styles.table} style={{ marginTop: 10 }}>
          <div
            style={{
              display: 'grid', gridTemplateColumns: '150px 180px 1fr 1fr', gap: 10,
              padding: '9px 14px', borderBottom: '1px solid var(--sc-border)',
              color: 'var(--sc-text-muted)', font: '600 10.5px/1.2 var(--sc-font-body)',
              textTransform: 'uppercase', letterSpacing: '.04em',
            }}
          >
            <span>시각</span><span>이벤트</span><span>수행자 → 대상</span><span>내용</span>
          </div>
          {(events ?? []).map((ev) => (
            <div
              key={ev.id}
              style={{
                display: 'grid', gridTemplateColumns: '150px 180px 1fr 1fr', gap: 10,
                padding: '10px 14px', borderBottom: '1px solid var(--sc-border)',
                font: '500 12px/1.5 var(--sc-font-body)', alignItems: 'baseline',
              }}
            >
              <span style={{ color: 'var(--sc-text-muted)' }}>
                {ev.created_at ? formatTimestamp(ev.created_at) : '—'}
              </span>
              <span style={{ fontWeight: 700 }}>{AUDIT_EVENT_LABELS[ev.event_type] ?? ev.event_type}</span>
              <span>
                {emailLocal(ev.actor_email ?? '')} → {ev.target ?? '—'}
              </span>
              <span style={{ color: 'var(--sc-text-muted)', wordBreak: 'break-all' }}>
                {Object.keys(ev.payload).length ? JSON.stringify(ev.payload) : '—'}
              </span>
            </div>
          ))}
          {!(events ?? []).length && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--sc-text-muted)', font: '500 12px/1.5 var(--sc-font-body)' }}>
              {q.trim() ? '검색 조건에 맞는 감사 로그가 없어요.' : '아직 기록된 감사 로그가 없어요.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
